import type {
    DayMoment,
    FaqItem,
    HostingPackage,
    InfoItem,
    MenuCourse,
    PageKey,
    PageMeta,
    Programme,
    Stat
} from '@/types'

import { at, library } from '@/constants/media'
import { galleryCollection } from '@/constants/experience'

/* ------------------------------------------------------------------ */
/* Page registry                                                        */
/* ------------------------------------------------------------------ */

export const pages: Record<PageKey, PageMeta> = {
    course: {
        href: '/course',
        index: '01',
        eyebrow: 'The course',
        title: 'The Course',
        accent: 'The Championship 18, walked for two years before a single hole was drawn.',
        seoTitle: 'The course',
        description: 'The Championship 18 at Morrow Golf Park: par 72, 7,048 yards along dune, pine and heath on the Alentejo coast, with the full routing, scorecard and signature holes.',
        teaser: 'Eighteen holes along dune, pine and heath.',
        image: at(library.coursePanorama, 'center 72%'),
        facts: [
            { label: 'Par', value: '72' },
            { label: 'Yards', value: '7,048' },
            { label: 'Tee sets', value: '4' }
        ]
    },
    clubhouse: {
        href: '/clubhouse',
        index: '02',
        eyebrow: 'Clubhouse',
        title: 'Clubhouse',
        accent: 'Set into the slope above the 18th, built from the ground it stands on.',
        seoTitle: 'Clubhouse and architecture',
        description: 'Inside the Morrow clubhouse: rammed earth walls, a 64 metre timber canopy, the Pavilion, the members’ lounge and a terrace facing the 18th green.',
        teaser: 'A building that steps back so the land can step forward.',
        image: at(library.clubhouseCarts, '34% center'),
        facts: [
            { label: 'Under one roof', value: '1,850 m²' },
            { label: 'Timber canopy', value: '64 m' },
            { label: 'Solar by day', value: '100%' }
        ]
    },
    academy: {
        href: '/academy',
        index: '03',
        eyebrow: 'Academy',
        title: 'The Academy',
        accent: 'Practice, measured to the millimetre, from first light to floodlight.',
        seoTitle: 'Academy and practice',
        description: 'The Morrow Academy: a 48-bay tracked range, short game park, putting lab, performance studio, floodlit Short Nine and coaching programmes with PGA professionals.',
        teaser: 'Practice, measured to the millimetre.',
        image: at(library.firstLight, 'center 45%'),
        facts: [
            { label: 'Tracked bays', value: '48' },
            { label: 'PGA pros', value: '11' },
            { label: 'Short game', value: '3 ha' }
        ]
    },
    membership: {
        href: '/membership',
        index: '04',
        eyebrow: 'Membership',
        title: 'Membership',
        accent: 'A club for the long game, capped at 650 members.',
        seoTitle: 'Membership',
        description: 'Membership at Morrow Golf Park: Full, International, Weekday and Academy memberships, what every membership includes and how to apply.',
        teaser: 'A club for the long game.',
        image: at(library.membersWalking, 'center 42%'),
        facts: [
            { label: 'Members', value: '650' },
            { label: 'Tee gaps', value: '12 min' },
            { label: 'Book ahead', value: '21 days' }
        ]
    },
    dining: {
        href: '/dining',
        index: '05',
        eyebrow: 'Dining & stays',
        title: 'Dining & Stays',
        accent: 'Long lunches, late light and twenty-four lodges among the pines.',
        seoTitle: 'Dining and stays',
        description: 'Dining at Morrow: the Terrace, the Cellar Room tasting menu, the Halfway House and twenty-four timber lodges among the pines.',
        teaser: 'Long lunches. Late light.',
        image: at(library.cliffTerrace, '22% center'),
        facts: [
            { label: 'Venues', value: '4' },
            { label: 'Terrace seats', value: '96' },
            { label: 'Lodges', value: '24' }
        ]
    },
    events: {
        href: '/events',
        index: '06',
        eyebrow: 'Events',
        title: 'Events',
        accent: 'Invitationals, member competitions and private days on our land.',
        seoTitle: 'Events and tournaments',
        description: 'The 2026 and 2027 tournament calendar at Morrow Golf Park, plus golf days, weddings and corporate retreats across the estate.',
        teaser: 'The calendar, and your own day on our land.',
        image: at(library.terraceDining, '72% center'),
        facts: [
            { label: 'Seated guests', value: '240' },
            { label: 'Shotgun holes', value: '18' },
            { label: 'Season', value: '26 / 27' }
        ]
    },
    nature: {
        href: '/nature',
        index: '07',
        eyebrow: 'Nature & lifestyle',
        title: 'Nature & Lifestyle',
        accent: 'Heath, pine, reservoir and an empty beach, all a short walk from the first tee.',
        seoTitle: 'Nature and lifestyle',
        description: 'Life beyond the fairways at Morrow: restored heathland, the reservoir, 31 km of pine trails, an empty beach and a day that runs from first swim to late dinner.',
        teaser: 'Golf is only half the day.',
        image: at(library.goldenCourse, 'center 60%'),
        facts: [
            { label: 'Heath restored', value: '142 ha' },
            { label: 'Bird species', value: '86' },
            { label: 'Trails', value: '31 km' }
        ]
    },
    gallery: {
        href: '/gallery',
        index: '08',
        eyebrow: 'Gallery',
        title: 'Gallery',
        accent: 'The estate in every light, from the first tee to the last terrace table.',
        seoTitle: 'Gallery',
        description: 'Photographs of Morrow Golf Park: the course, the clubhouse, the Academy and life on the Alentejo coast.',
        teaser: 'The estate in every light.',
        image: at(library.fairwayPanorama, 'center 58%'),
        facts: [
            { label: 'Photographs', value: String(galleryCollection.length) },
            { label: 'Holes', value: '18' },
            { label: 'Seasons', value: '4' }
        ]
    },
    visit: {
        href: '/visit',
        index: '09',
        eyebrow: 'Visit & book',
        title: 'Visit & Book',
        accent: 'Seventy minutes from Lisbon, a world away from it.',
        seoTitle: 'Visit and book',
        description: 'How to reach Morrow Golf Park on the Alentejo coast, opening hours, and requests for tee times, lessons, dining, stays and events.',
        teaser: 'Find your way to Morrow.',
        image: at(library.clubhouseArrival, '38% center'),
        facts: [
            { label: 'From Lisbon', value: '70 min' },
            { label: 'Helipad', value: 'On site' },
            { label: 'The beach', value: '12 min' }
        ]
    },
    contact: {
        href: '/contact',
        index: '10',
        eyebrow: 'Contact',
        title: 'Contact',
        accent: 'Write to the concierge. A person reads every message and replies within a day.',
        seoTitle: 'Contact',
        description: 'Contact Morrow Golf Park on the Alentejo coast: send the concierge team a message about membership, visiting, events, the Academy, dining or the Lodges.',
        teaser: 'Write to the concierge.',
        image: at(library.greenAtSunset, '85% center'),
        facts: [
            { label: 'Reply within', value: '1 day' },
            { label: 'Languages', value: '3' },
            { label: 'Concierge', value: '07:00 to 23:00' }
        ]
    }
}

const pageOrder: PageKey[] = ['course', 'clubhouse', 'academy', 'membership', 'dining', 'events', 'nature', 'gallery', 'visit', 'contact']

/** The page that follows another in the site's reading order, wrapping at the end. */
export const nextPageAfter = (key: PageKey): PageMeta => {
    const position = pageOrder.indexOf(key)
    return pages[pageOrder[(position + 1) % pageOrder.length]]
}

/* ------------------------------------------------------------------ */
/* The course                                                           */
/* ------------------------------------------------------------------ */

export const courseIntroStats: Stat[] = [
    { value: 4, label: 'sets of tees, from 5,131 to 7,048 yards' },
    { value: 12, suffix: ' min', label: 'between tee times, so the course never feels busy' },
    { value: 2, label: 'years walking the estate before the routing was drawn' },
    { value: 0, label: 'tonnes of imported sand, every bunker is native dune' }
]

export const teeSets = [
    { name: 'Championship', colour: '#1b1b19', factor: 1 },
    { name: 'Members', colour: '#f8f6f1', factor: 0.925 },
    { name: 'Forward', colour: '#6f7458', factor: 0.845 },
    { name: 'Short', colour: '#a5683f', factor: 0.728 }
]

export const playingInfo: InfoItem[] = [
    {
        title: 'Tee times',
        description: 'Members book twenty-one days ahead and guests fourteen, with twelve minutes between every group.',
        meta: 'Tee sheet from 07:00'
    },
    {
        title: 'Caddies',
        description: 'Forecaddies who know every break on every green, available for any round on the Championship 18.',
        meta: 'Request with your tee time'
    },
    {
        title: 'Carts',
        description: 'Electric carts with course GPS, kept to the paths across the heathland holes to protect the turf.',
        meta: 'Walking always welcome'
    },
    {
        title: 'Pace of play',
        description: 'A round here takes four hours and ten minutes. Our marshals quietly help keep it that way.',
        meta: '4 h 10 min'
    },
    {
        title: 'Dress',
        description: 'Collared shirts, tailored shorts or trousers and soft spikes on the course. The clubhouse is relaxed.',
        meta: 'Smart casual indoors'
    },
    {
        title: 'Handicaps',
        description: 'Visitors play the Championship tees with a handicap of 24 or better. The forward tees are open to all.',
        meta: 'Certificate on arrival'
    }
]

/* ------------------------------------------------------------------ */
/* Clubhouse                                                            */
/* ------------------------------------------------------------------ */

export const clubhouseIntroStats: Stat[] = [
    { value: 38, suffix: ' cm', label: 'thick rammed earth walls that hold the cool of the night' },
    { value: 4, label: 'courtyards bringing light and air deep into the building' },
    { value: 212, label: 'solar panels hidden within the timber canopy' },
    { value: 3, suffix: ' m', label: 'of deep shade along the terrace at midday' }
]

export const clubhouseSpaces: InfoItem[] = [
    {
        title: 'The Pavilion',
        description: 'The main room of the clubhouse, opening through a wall of frameless glass onto the 18th green.',
        meta: 'Up to 240 seated'
    },
    {
        title: 'Members’ lounge',
        description: 'Deep chairs, an open fire in winter and a library of the original routing drawings.',
        meta: 'Members and guests'
    },
    {
        title: 'Locker rooms',
        description: 'Timber lockers, a steam room and cold plunge, with towels and shoes cared for between rounds.',
        meta: 'Club care included'
    },
    {
        title: 'Pro shop',
        description: 'Apparel made for the climate here, rental sets from every major maker and same-day regripping.',
        meta: '07:00 to 19:00'
    },
    {
        title: 'The Map Room',
        description: 'A private dining room for twelve, lined with the hand-drawn plans of every hole on the estate.',
        meta: 'Private dining for 12'
    },
    {
        title: 'The Terrace',
        description: 'All-day dining under the timber canopy with the whole back nine laid out below.',
        meta: '96 seats inside and out'
    }
]

export const clubhouseTimeline: InfoItem[] = [
    {
        title: 'The first walk',
        description: 'Two architects and a greenkeeper spend a full year walking the estate before a single hole is drawn.',
        meta: '2019'
    },
    {
        title: 'Restoring the heath',
        description: 'One hundred and forty-two hectares are cleared of invasive acacia and replanted with native heather.',
        meta: '2021'
    },
    {
        title: 'Pressing the walls',
        description: 'The rammed earth walls rise layer by layer from soil excavated for the foundations.',
        meta: '2023'
    },
    {
        title: 'Doors open',
        description: 'The first tee time goes out at seven on a spring morning, and the terrace fills by noon.',
        meta: '2026'
    }
]

/* ------------------------------------------------------------------ */
/* Academy                                                              */
/* ------------------------------------------------------------------ */

export const academyIntroStats: Stat[] = [
    { value: 320, suffix: ' m', label: 'range with target greens shaped like the course' },
    { value: 5, label: 'practice venues, from the range to the floodlit Short Nine' },
    { value: 12, label: 'week Performance Plan for serious improvers' },
    { value: 90, suffix: ' min', label: 'custom fitting session on the launch monitor' }
]

export const programmes: Programme[] = [
    {
        name: 'Private lesson',
        length: '60 minutes',
        description: 'One hour with a PGA professional on any part of your game, recorded and sent to your phone.',
        includes: ['Video analysis', 'Launch data', 'Practice plan'],
        focus: 'Full swing'
    },
    {
        name: 'Performance Plan',
        length: '12 weeks',
        description: 'An assessment, eight lessons and two playing sessions on the course, built around clear targets.',
        includes: ['3D motion capture', 'Two on-course lessons', 'Fitting review'],
        focus: 'Performance Studio assessment'
    },
    {
        name: 'Short Game School',
        length: '3 mornings',
        description: 'Chipping, bunker play and putting in the Short Game Park and Putting Lab, in groups of four.',
        includes: ['Groups of four', 'Putting Lab session', 'Lunch on the Terrace'],
        focus: 'Short game'
    },
    {
        name: 'First Swing',
        length: '4 sessions',
        description: 'For complete beginners. Four relaxed sessions, then your first round together on the Short Nine.',
        includes: ['Clubs provided', 'Rules and etiquette', 'Round on the Short Nine'],
        focus: 'Full swing'
    },
    {
        name: 'Junior Academy',
        length: 'Termly',
        description: 'Weekend coaching for ages six to seventeen, grouped by age and ability, with a junior tour in summer.',
        includes: ['Ages 6 to 17', 'Weekend groups', 'Summer junior tour'],
        focus: 'Junior coaching'
    },
    {
        name: 'Custom fitting',
        length: '90 minutes',
        description: 'Every major maker, every shaft, measured on the launch monitor and then on the course itself.',
        includes: ['All major makers', 'Launch monitor', 'On-course check'],
        focus: 'Custom club fitting'
    }
]

export const studioSteps: InfoItem[] = [
    {
        title: 'Capture',
        description: 'Fifteen minutes of 3D motion capture and launch monitoring records how your body and the ball really move.'
    },
    {
        title: 'Understand',
        description: 'Your coach walks you through the numbers that matter, and leaves out the ones that do not.'
    },
    {
        title: 'Train',
        description: 'Drills built for your swing, on the range, in the Short Game Park or in the Putting Lab.'
    },
    {
        title: 'Review',
        description: 'A report lands on your phone before you reach the car, with your next session already suggested.'
    }
]

export const academyFaqs: FaqItem[] = [
    {
        question: 'Do I need to be a member to take lessons?',
        answer: 'No. Lessons, programmes, fitting and the practice facilities are open to everyone. Members simply book further ahead and at member rates.'
    },
    {
        question: 'What should I bring?',
        answer: 'Your clubs and golf shoes if you have them. If not, rental sets and shoes are waiting at the pro shop at no charge for First Swing players.'
    },
    {
        question: 'Can children join the Junior Academy mid-term?',
        answer: 'Yes, whenever there is space in their age group. A coach meets every new junior for a short assessment first.'
    },
    {
        question: 'Is the range open in the evening?',
        answer: 'The range is floodlit and open until ten at night, and the Short Nine until eleven.'
    }
]

/* ------------------------------------------------------------------ */
/* Membership                                                           */
/* ------------------------------------------------------------------ */

export const membershipIntroStats: Stat[] = [
    { value: 650, label: 'members at most, so the course always has room' },
    { value: 12, suffix: ' min', label: 'between every group on the tee sheet' },
    { value: 21, label: 'days ahead for member tee times' },
    { value: 24, label: 'accompanied guest rounds a year for Full members' }
]

export const membershipIncludes: InfoItem[] = [
    {
        title: 'Priority tee sheet',
        description: 'Member tee times open before guest times, with a quiet hour reserved every morning.',
        meta: 'From 07:00 daily'
    },
    {
        title: 'Guest privileges',
        description: 'Bring friends and family at member-guest rates, with their lunch on the Terrace on us.',
        meta: 'Member-guest rates'
    },
    {
        title: 'The Lodges',
        description: 'Member rates on all twenty-four lodges, for yourself or for visiting friends.',
        meta: 'Priority at weekends'
    },
    {
        title: 'Member calendar',
        description: 'Monthly medals, the Founders’ Cup, winter foursomes and long summer evenings on the Short Nine.',
        meta: 'Twelve competitions a year'
    },
    {
        title: 'Club care',
        description: 'A timber locker, bag storage and clubs cleaned after every round, with regripping in the pro shop.',
        meta: 'Included'
    },
    {
        title: 'Dining',
        description: 'Member rates at the Terrace and the Cellar Room, and first call on the Map Room for private dinners.',
        meta: 'Member rates'
    }
]

export const membershipSteps: InfoItem[] = [
    {
        title: 'Enquire',
        description: 'Tell us a little about your golf and the membership you have in mind. We reply within a day.'
    },
    {
        title: 'Visit and play',
        description: 'Spend a day with us: a round with one of the team, lunch on the Terrace and a walk through the clubhouse.'
    },
    {
        title: 'Meet the committee',
        description: 'An informal conversation over coffee. We simply want to know you will love the place as much as we do.'
    },
    {
        title: 'Welcome',
        description: 'Your locker is named, your clubs are fitted and your first member tee time is waiting.'
    }
]

export const membershipFaqs: FaqItem[] = [
    {
        question: 'How many members will Morrow have?',
        answer: 'Never more than 650 across all categories. When the club is full, applications join a waiting list in the order they arrive.'
    },
    {
        question: 'Can I play the course before applying?',
        answer: 'Yes. Every applicant is invited for a day on the estate, including a round on the Championship 18 as our guest.'
    },
    {
        question: 'Do you need a handicap to join?',
        answer: 'No handicap is required for Weekday or Academy membership. Full and International members play the Championship tees with a handicap of 28 or better.'
    },
    {
        question: 'Is there a family membership?',
        answer: 'Partners of Full members can join at a reduced rate, and children of members are welcome in the Junior Academy at no extra cost.'
    },
    {
        question: 'How are fees shared?',
        answer: 'Personally, once we receive your enquiry. Fees are annual, with a one-time joining fee for Full and International membership.'
    }
]

/* ------------------------------------------------------------------ */
/* Dining & stays                                                       */
/* ------------------------------------------------------------------ */

export const diningIntroStats: Stat[] = [
    { value: 40, suffix: ' km', label: 'is the furthest anything on the plate travels' },
    { value: 4, label: 'places to eat, drink and stay across the estate' },
    { value: 96, label: 'seats on the Terrace, inside and out' },
    { value: 24, label: 'timber lodges among the pines' }
]

export const terraceMenu: MenuCourse[] = [
    {
        title: 'To start',
        dishes: [
            { name: 'Morning oysters', description: 'From the estuary, with green apple and a little sea fennel.' },
            { name: 'Garden tomatoes', description: 'Warm from the estate garden, with sheep’s cheese and olive oil from the valley.' },
            { name: 'Cured presa', description: 'Black pork, aged for eighteen months, sliced to order.' }
        ]
    },
    {
        title: 'From the fire',
        dishes: [
            { name: 'Whole sea bass', description: 'Cooked over vine wood and finished with lemon and wild fennel.' },
            { name: 'Carabineiro prawns', description: 'Split and grilled, with garlic, chilli and bread for the juices.' },
            { name: 'Aged sirloin', description: 'Alentejo beef, sixty days on the bone, with salt-baked potatoes.' }
        ]
    },
    {
        title: 'To finish',
        dishes: [
            { name: 'Orange and almond cake', description: 'Still warm, with a spoon of cold cream.' },
            { name: 'Valley cheeses', description: 'Three from within forty kilometres, with fig bread and honey.' }
        ]
    }
]

export const lodgeTypes: InfoItem[] = [
    {
        title: 'Pine Lodge',
        description: 'One bedroom under the pines, with a terrace, an open fire and a cart parked at the door.',
        meta: 'Sleeps 2'
    },
    {
        title: 'Heath Lodge',
        description: 'Two bedrooms looking over the heath to the ocean, with an outdoor shower and a kitchen for long breakfasts.',
        meta: 'Sleeps 4'
    },
    {
        title: 'Dune House',
        description: 'Four bedrooms, a pool in the dunes and a private chef on request. Made for golf trips and families.',
        meta: 'Sleeps 8'
    }
]

/* ------------------------------------------------------------------ */
/* Events                                                               */
/* ------------------------------------------------------------------ */

export const eventsIntroStats: Stat[] = [
    { value: 80, label: 'players for a full shotgun start' },
    { value: 3, label: 'rooms for dinners, from 12 guests to 240' },
    { value: 6, label: 'open tournaments in the 2026 and 2027 season' },
    { value: 1, label: 'events team from the first call to the last dance' }
]

export const hostingPackages: HostingPackage[] = [
    {
        name: 'Golf days',
        description: 'The Championship 18 reserved for your group, a format of your choosing and a long dinner in the Pavilion to finish.',
        details: ['24 to 80 players', 'Shotgun or tee times', 'Prize giving on the Terrace'],
        image: at(library.greenAtSunset, '80% center')
    },
    {
        name: 'Weddings',
        description: 'Vows on the cliff above the coast holes, dinner under the timber canopy and the lodges for everyone who stays.',
        details: ['Up to 200 guests', 'Ceremony on the cliff', 'Lodges for the wedding party'],
        image: at(library.cliffTerrace, '30% center')
    },
    {
        name: 'Retreats',
        description: 'Quiet rooms for thinking, a course for talking and a table for deciding. Two to four days on the estate.',
        details: ['12 to 60 guests', 'The Map Room and Pavilion', 'Coaching sessions included'],
        image: at(library.clubhouseCarts, '30% center')
    }
]

export const golfDayIncludes: InfoItem[] = [
    {
        title: 'Shotgun start',
        description: 'All eighteen holes held for your group, with starters, marshals and forecaddies if you want them.'
    },
    {
        title: 'Your format',
        description: 'Scramble, Stableford or match play, set up and scored by our team from the first tee.'
    },
    {
        title: 'Live scoring',
        description: 'Scores entered on the carts appear on the leaderboard in the Pavilion as the day unfolds.'
    },
    {
        title: 'Hospitality',
        description: 'Breakfast before, the Halfway House at the turn and dinner in the Pavilion once the cards are in.'
    },
    {
        title: 'Prize giving',
        description: 'Trophies, nearest the pin and longest drive, presented on the Terrace as the sun goes down.'
    },
    {
        title: 'Overnight',
        description: 'The lodges held for your guests at event rates, with breakfast and a morning nine for early risers.'
    }
]

/* ------------------------------------------------------------------ */
/* Nature & lifestyle                                                   */
/* ------------------------------------------------------------------ */

export const natureIntroStats: Stat[] = [
    { value: 63, suffix: '%', label: 'of the estate left as native habitat' },
    { value: 4, suffix: ' km', label: 'of beach, twelve minutes through the dunes' },
    { value: 11, label: 'barn owl boxes placed across the estate' },
    { value: 0, label: 'synthetic pesticides on the roughs and heath' }
]

export const dayAtMorrow: DayMoment[] = [
    {
        time: '06:30',
        title: 'A swim before breakfast',
        description: 'Mist on the reservoir, the water still cold, coffee waiting on the jetty.',
        image: library.reservoirMorning
    },
    {
        time: '07:12',
        title: 'The first tee',
        description: 'Dew on the fairway, the only footprints yours, the whole front nine ahead.',
        image: at(library.firstLight, 'center 40%')
    },
    {
        time: '12:30',
        title: 'Lunch on the Terrace',
        description: 'Fish from the morning boats and a glass of something cold from the valley.',
        image: at(library.terraceDining, '78% center')
    },
    {
        time: '15:00',
        title: 'An hour in the Putting Lab',
        description: 'The one part of the game that always rewards another hour of practice.',
        image: at(library.readingThePutt, '38% center')
    },
    {
        time: '18:40',
        title: 'Twilight nine',
        description: 'The back nine at golden hour, when the heath glows and the flags throw long shadows.',
        image: at(library.greenAtSunset, '82% center')
    },
    {
        time: '21:00',
        title: 'Dinner above the coast',
        description: 'The last light over the bay, and a table on the cliff that no one wants to leave.',
        image: at(library.cliffTerrace, '25% center')
    }
]

export const seasons: InfoItem[] = [
    {
        title: 'Spring',
        description: 'The heath flowers purple and gold, and the course plays long and green.',
        meta: 'March to May'
    },
    {
        title: 'Summer',
        description: 'Long light and firm fairways. Twilight tee times run until half past nine.',
        meta: 'June to August'
    },
    {
        title: 'Autumn',
        description: 'Migrating birds pass over the reservoir and the greens are at their very best.',
        meta: 'September to November'
    },
    {
        title: 'Winter',
        description: 'Mild days around sixteen degrees, quiet tee sheets and fires in the lounge.',
        meta: 'December to February'
    }
]

/* ------------------------------------------------------------------ */
/* Visit & book                                                         */
/* ------------------------------------------------------------------ */

export const visitIntroStats: Stat[] = [
    { value: 70, suffix: ' min', label: 'by car from Lisbon Airport' },
    { value: 20, suffix: ' min', label: 'by helicopter to the estate helipad' },
    { value: 12, suffix: ' min', label: 'on foot from the clubhouse to the beach' },
    { value: 24, label: 'lodges if one day is not enough' }
]

export const gettingHere: InfoItem[] = [
    {
        title: 'By car',
        description: 'Take the A2 south from Lisbon, then the coast road for the final twenty minutes. The estate gate is signed Morrow.',
        meta: 'About 105 km'
    },
    {
        title: 'Airport transfer',
        description: 'Our concierge arranges a private car from Lisbon Airport, timed to your flight and waiting at arrivals.',
        meta: 'Book 24 hours ahead'
    },
    {
        title: 'By helicopter',
        description: 'Twenty minutes from Lisbon to the estate helipad beside the range, arranged through the concierge.',
        meta: 'Book 48 hours ahead'
    },
    {
        title: 'By train',
        description: 'Trains run from Lisbon to Grândola in about ninety minutes, and we collect you from the station.',
        meta: 'Station pick-up'
    },
    {
        title: 'Charging',
        description: 'Twelve fast chargers in the car park, shaded by the solar canopy that powers them.',
        meta: 'Free for guests'
    },
    {
        title: 'On arrival',
        description: 'Leave your bag at the clubhouse drop. It will be on a cart at the first tee by the time you are.',
        meta: 'Bag drop from 06:30'
    }
]

export const visitFaqs: FaqItem[] = [
    {
        question: 'Can visitors play the Championship 18?',
        answer: 'Yes. Guest tee times open fourteen days ahead. Send a request with your preferred date and time and we will confirm within a working day.'
    },
    {
        question: 'Can I hire clubs?',
        answer: 'Rental sets from every major maker are kept at the pro shop, fitted to you on arrival.'
    },
    {
        question: 'What is the dress code?',
        answer: 'Collared shirts, tailored shorts or trousers and soft spikes on the course. The clubhouse and terrace are smart casual.'
    },
    {
        question: 'Are children welcome?',
        answer: 'Always. Juniors play the forward tees with an adult, and the Short Nine and beach are made for families.'
    },
    {
        question: 'What if I need to cancel?',
        answer: 'Tee times can be changed or cancelled free of charge up to 48 hours before play. After that we will always try to find you another time.'
    }
]
